'use client';

/**
 * Providers Component
 * Wraps the application with necessary context providers
 */

// Initialize Sentry client-side (Turbopack doesn't auto-inject the config)
import '../../sentry.client.config';

import { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/query-client';
import { AuthProvider } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/errors/ErrorBoundary';
import { VersionManager } from '@/components/VersionManager';

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {children}

          {/* Version manager - handles chunk errors and version mismatches */}
          <VersionManager />

          {/* React Query Devtools - only in development */}
          <ReactQueryDevtools initialIsOpen={false} position="left" />

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1E293B',
                color: '#F1F5F9',
                border: '1px solid #334155',
                maxWidth: '480px',
                padding: '12px 16px',
                wordBreak: 'break-word',
                lineHeight: '1.5',
              },
              success: {
                iconTheme: {
                  primary: '#B8FF00',
                  secondary: '#1E293B',
                },
              },
              error: {
                duration: 6000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#1E293B',
                },
              },
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
